import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Listing, OpeningHoursResult } from '../../types';
import { getArea } from '../../config/areas';
import { navTargetOf } from '../../utils/geo';
import { displayAddress, pickLang } from '../../utils/listingDisplay';
import NavigateButton from './NavigateButton';
import { listingHref } from './ListingCard';
import { OpenStatusBadge } from './StatusBadge';

/** Card shown when a pin is tapped: name, status, Details and Navigate (plan 7). */
export default function MapListingPopup({ listing, hours }: { listing: Listing; hours: OpeningHoursResult | undefined }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const address = displayAddress(listing, isHebrew);
  const sub = 'subcategory' in listing ? listing.subcategory : undefined;
  const target = navTargetOf(listing.location, getArea(listing.location?.areaId ?? ''), isHebrew);

  return (
    <div className="min-w-[180px] max-w-[240px] space-y-1.5" dir={isHebrew ? 'rtl' : 'ltr'}>
      {listing.photoUrl && (
        <img src={listing.photoUrl} alt="" className="w-full h-24 object-cover rounded-[10px]" />
      )}
      <p className="font-semibold text-ink text-sm leading-snug">{pickLang(listing.nameEn, listing.nameHe, isHebrew)}</p>
      {address && (
        <p className="text-xs text-ash">
          {address}
          {listing.location?.showExactLocation === false && ` (${t('directory.map.approxArea')})`}
        </p>
      )}
      <div className="flex flex-wrap gap-1">
        <span className="text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium">
          {sub ? t(`directory.sub.${sub}`) : t(`directory.category.${listing.category}`)}
        </span>
        {listing.category !== 'shul' && <OpenStatusBadge listing={listing} result={hours} />}
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Link
          to={listingHref(listing)}
          className="text-xs font-medium border border-ink/15 !text-ink rounded-full px-3 py-1.5 hover:bg-fog"
        >
          {t('directory.action.details')}
        </Link>
        <NavigateButton target={target} variant="compact" />
      </div>
    </div>
  );
}
